import { type FormEvent, type ReactNode, useMemo, useState } from "react";

import { useSearchParams } from "react-router-dom";

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { History, Megaphone, PenLine, Send, User, Users } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { adminApi } from "@/api/endpoints";
import { ColumnVisibility, DataTable, TablePagination } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { PageHeader } from "@/components/shared/page-header";
import { Panel } from "@/components/shared/panel";
import { Segmented } from "@/components/shared/segmented";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { currentLanguage } from "@/configs/i18n";
import { queryKeys } from "@/configs/query-keys";
import { useColumnVisibility } from "@/hooks/use-column-visibility";
import { useErrorToast } from "@/hooks/use-error-toast";
import { useUrlPagination } from "@/hooks/use-url-pagination";
import {
  type AnnouncementDraft,
  type AnnouncementErrors,
  type AnnouncementTarget,
  toAnnouncementInput,
  validateAnnouncement,
} from "@/lib/announcement";
import { formatDateTime, formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { Announcement } from "@/types/api";

const BODY_LIMIT = 2000;

export function AnnouncementsPage() {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader
        title={t("announcements.title")}
        subtitle={t("announcements.subtitle")}
        icon={Megaphone}
        tone="violet"
      />
      <div className="grid gap-6 xl:min-h-0 xl:flex-1 xl:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
        <AnnouncementForm />
        <AnnouncementHistory />
      </div>
    </>
  );
}

function AnnouncementForm() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const showError = useErrorToast();
  const prefilled = params.get("to") ?? "";
  const [draft, setDraft] = useState<AnnouncementDraft>({
    title: "",
    body: "",
    target: prefilled ? "one" : "all",
    telegramUserId: prefilled,
  });
  const [errors, setErrors] = useState<AnnouncementErrors>({});
  const [confirming, setConfirming] = useState(false);

  const send = useMutation({
    mutationFn: adminApi.announce,
    onSuccess: created => {
      toast.success(t("announcements.sent", { count: created.recipientCount }));
      setDraft(prev => ({ ...prev, title: "", body: "" }));
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.announcementsAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.deliveriesAll });
    },
    onError: error => {
      setConfirming(false);
      showError(error);
    },
  });

  const update = (patch: Partial<AnnouncementDraft>) => setDraft(prev => ({ ...prev, ...patch }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = validateAnnouncement(draft);
    setErrors(found);
    if (Object.keys(found).length === 0) setConfirming(true);
  };

  const used = draft.body.length / BODY_LIMIT;

  return (
    <Panel
      icon={PenLine}
      tone="bg-tile-violet text-tile-violet-strong"
      title={t("announcements.new")}
      className="thin-scrollbar self-start xl:max-h-full xl:overflow-y-auto"
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <div className="space-y-2">
          <Label>{t("announcements.target")}</Label>
          <Segmented<AnnouncementTarget>
            label={t("announcements.target")}
            value={draft.target}
            onChange={target => update({ target })}
            block
            options={[
              { value: "all", label: t("announcements.targetAll") },
              { value: "one", label: t("announcements.targetOne") },
            ]}
          />
        </div>

        <AnimatePresence initial={false}>
          {draft.target === "one" ? (
            <motion.div
              key="target"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <Field id="ann-tg" label={t("announcements.targetId")} error={errors.telegramId}>
                <Input
                  id="ann-tg"
                  inputMode="numeric"
                  value={draft.telegramUserId}
                  aria-invalid={!!errors.telegramId}
                  onChange={event => update({ telegramUserId: event.target.value })}
                />
              </Field>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <Field id="ann-title" label={t("announcements.formTitle")} error={errors.title}>
          <Input
            id="ann-title"
            maxLength={200}
            value={draft.title}
            aria-invalid={!!errors.title}
            onChange={event => update({ title: event.target.value })}
          />
        </Field>

        <Field id="ann-body" label={t("announcements.formBody")} error={errors.body}>
          <Textarea
            id="ann-body"
            rows={6}
            maxLength={BODY_LIMIT}
            value={draft.body}
            aria-invalid={!!errors.body}
            onChange={event => update({ body: event.target.value })}
          />
          <div className="flex items-center gap-2">
            <div className="bg-muted h-1 flex-1 overflow-hidden rounded-full">
              <motion.div
                className={cn("h-full rounded-full", used > 0.9 ? "bg-destructive" : "bg-primary")}
                animate={{ width: `${Math.min(100, used * 100)}%` }}
                transition={{ type: "spring", stiffness: 200, damping: 30 }}
              />
            </div>
            <p className="text-muted-foreground text-xs tabular-nums">
              {draft.body.length}/{BODY_LIMIT}
            </p>
          </div>
        </Field>

        {/* Jonli ko'rinish — Telegram'da xabar qanday kelishi. */}
        <AnimatePresence initial={false}>
          {draft.title.trim() || draft.body.trim() ? (
            <motion.div
              key="preview"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              className="bg-tile-sky/60 rounded-2xl rounded-bl-md p-3 text-sm"
            >
              <p className="text-muted-foreground mb-1 text-[11px] font-medium uppercase">
                {t("announcements.preview")}
              </p>
              <p className="font-medium">📣 {draft.title.trim()}</p>
              <p className="text-foreground/75 mt-1 line-clamp-4 whitespace-pre-wrap">
                {draft.body.trim()}
              </p>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <p className="text-muted-foreground text-xs">{t("announcements.hint")}</p>

        <Button type="submit" size="lg" className="w-full rounded-xl">
          <Send />
          {t("announcements.send")}
        </Button>
      </form>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("announcements.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {draft.target === "all"
                ? t("announcements.confirmAll")
                : t("announcements.confirmOne", { id: draft.telegramUserId.trim() })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="bg-muted rounded-lg p-3 text-sm">
            <p className="font-medium">📣 {draft.title.trim()}</p>
            <p className="text-muted-foreground mt-1 whitespace-pre-wrap">{draft.body.trim()}</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={send.isPending}
              onClick={event => {
                event.preventDefault();
                send.mutate(toAnnouncementInput(draft));
              }}
            >
              {t("announcements.send")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
}

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? (
        <motion.p
          initial={{ opacity: 0, x: -6 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-destructive text-xs"
          role="alert"
        >
          {t(error)}
        </motion.p>
      ) : null}
    </div>
  );
}

function AnnouncementHistory() {
  const { t } = useTranslation();
  const { page, limit, setPage, setLimit } = useUrlPagination();
  const { columnVisibility, onColumnVisibilityChange } = useColumnVisibility("announcements");
  const filters = { page, limit };
  const history = useQuery({
    queryKey: queryKeys.announcements(filters),
    queryFn: () => adminApi.announcements(filters),
    placeholderData: keepPreviousData,
  });

  const columns = useAnnouncementColumns();
  const table = useReactTable({
    data: history.data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
    state: { columnVisibility, pagination: { pageIndex: page - 1, pageSize: limit } },
    onColumnVisibilityChange,
    manualPagination: true,
    enableSorting: false,
    enableColumnFilters: false,
    pageCount: history.data?.meta.totalPages ?? 0,
  });

  return (
    <section className="flex min-h-0 min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-semibold">
          <History className="text-muted-foreground size-4" aria-hidden />
          {t("announcements.history")}
        </h2>
        <ColumnVisibility table={table} />
      </div>
      <DataTable
        table={table}
        isLoading={history.isPending}
        isFetching={history.isFetching}
        error={history.error}
        onRetry={() => void history.refetch()}
        empty={
          <EmptyState
            icon={Megaphone}
            title={t("announcements.emptyTitle")}
            text={t("announcements.emptyText")}
          />
        }
      />
      {history.data && history.data.meta.total > 0 ? (
        <TablePagination
          page={page}
          limit={limit}
          total={history.data.meta.total}
          totalPages={history.data.meta.totalPages}
          onPage={setPage}
          onLimit={setLimit}
        />
      ) : null}
    </section>
  );
}

function useAnnouncementColumns(): ColumnDef<Announcement>[] {
  const { t } = useTranslation();
  const lang = currentLanguage();
  return useMemo<ColumnDef<Announcement>[]>(
    () => [
      {
        id: "message",
        accessorKey: "title",
        header: t("announcements.formTitle"),
        enableHiding: false,
        meta: { label: t("announcements.formTitle"), width: 360 },
        cell: ({ row }) => (
          <div className="min-w-0 space-y-0.5">
            <p className="truncate font-medium">{row.original.title}</p>
            <p className="text-muted-foreground line-clamp-2 text-xs whitespace-pre-wrap">
              {row.original.body}
            </p>
          </div>
        ),
      },
      {
        id: "target",
        accessorKey: "targetUserId",
        header: t("announcements.target"),
        meta: { label: t("announcements.target"), width: 200 },
        cell: ({ row }) => {
          const one = row.original.targetUserId !== null;
          const Icon = one ? User : Users;
          return (
            <span
              className={cn(
                "inline-flex h-6 items-center gap-1.5 rounded-full px-2 text-xs font-medium whitespace-nowrap",
                one
                  ? "bg-tile-amber text-tile-amber-strong"
                  : "bg-tile-violet text-tile-violet-strong",
              )}
            >
              <Icon className="size-3.5" aria-hidden />
              {one ? t("announcements.toOne") : t("announcements.toAll")}
            </span>
          );
        },
      },
      {
        id: "recipients",
        accessorKey: "recipientCount",
        header: t("announcements.recipientsColumn"),
        meta: { label: t("announcements.recipientsColumn"), width: 130, align: "end" },
        cell: ({ row }) => (
          <span className="font-semibold tabular-nums">
            {formatNumber(row.original.recipientCount)}
          </span>
        ),
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: t("announcements.sentAt"),
        meta: { label: t("announcements.sentAt"), width: 200 },
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs whitespace-nowrap">
            {formatDateTime(row.original.createdAt, lang)}
          </span>
        ),
      },
    ],
    [t, lang],
  );
}
